import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { KPIService } from '../../services/kpiService';
import ModelBuilderDashboard from './ModelBuilderDashboard';
import DataPrepDashboard from './DataPrepDashboard';
import ManagerDashboard from './ManagerDashboard';
import './KPIBoard.css';

/**
 * KPI Board - Main container component
 * Detects user role and displays appropriate dashboard
 * Defaults to monthly periods with biweekly drill-down
 */
const KPIBoard = () => {
  const { user } = useAuth();
  const [dashboardType, setDashboardType] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [selectedBiweek, setSelectedBiweek] = useState(null); // null = full month

  // Determine user's dashboard type on mount
  useEffect(() => {
    if (user?.email) {
      const type = KPIService.getDashboardType(user.email);
      const empId = KPIService.getEmployeeIdFromEmail(user.email);
      setDashboardType(type);
      setEmployeeId(empId);

      // Get monthly periods (last 30 days + 12 calendar months of history)
      const periodList = KPIService.getMonthlyPeriods(13);
      setPeriods(periodList);
    }
  }, [user]);

  const handlePeriodChange = (e) => {
    const index = parseInt(e.target.value, 10);
    setSelectedPeriodIndex(index);
    setSelectedBiweek(null); // Reset to full month when switching months
  };

  const selectedPeriod = periods[selectedPeriodIndex];

  // Determine the active date range based on biweek selection
  const getActiveDateRange = () => {
    if (!selectedPeriod) return { startDate: null, endDate: null };

    if (selectedBiweek !== null && selectedPeriod.biweeks?.[selectedBiweek]) {
      return selectedPeriod.biweeks[selectedBiweek];
    }

    return { startDate: selectedPeriod.startDate, endDate: selectedPeriod.endDate };
  };

  const { startDate, endDate } = getActiveDateRange();

  if (!dashboardType || !selectedPeriod) {
    return (
      <div className="kpi-board">
        <div className="kpi-board-loading">
          Loading KPI Dashboard...
        </div>
      </div>
    );
  }

  const renderDashboard = () => {
    const periodLabel = selectedBiweek !== null && selectedPeriod.biweeks?.[selectedBiweek]
      ? selectedPeriod.biweeks[selectedBiweek].label
      : selectedPeriod.label;

    const props = {
      startDate,
      endDate,
      periodLabel,
      userEmail: user?.email,
    };

    switch (dashboardType) {
      case 'manager':
        return <ManagerDashboard {...props} />;
      case 'model_builder':
        return <ModelBuilderDashboard employeeId={employeeId} {...props} />;
      case 'data_prep':
        return <DataPrepDashboard {...props} />;
      default:
        return <ManagerDashboard {...props} />;
    }
  };

  return (
    <div className="kpi-board">
      {/* Header */}
      <div className="kpi-board-header">
        <div className="kpi-board-header-left">
          <h1 className="kpi-board-title">KPI Dashboard</h1>
          <p className="kpi-board-subtitle">
            {dashboardType === 'manager' ? 'Team Overview' :
             dashboardType === 'model_builder' ? 'Model Builder Metrics' :
             dashboardType === 'data_prep' ? 'Data Prep Metrics' :
             'Personal Metrics'}
          </p>
        </div>

        <div className="kpi-board-header-right">
          {/* Month Period Selector */}
          <div className="kpi-board-period-selector">
            <label className="kpi-board-period-label">Period:</label>
            <select
              value={selectedPeriodIndex}
              onChange={handlePeriodChange}
              className="kpi-board-period-select"
            >
              {periods.map((period, index) => (
                <option key={period.startDate} value={index}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Biweek Sub-Tabs */}
      {selectedPeriod?.biweeks && (
        <div className="kpi-biweek-tabs">
          <button
            className={`kpi-biweek-tab ${selectedBiweek === null ? 'active' : ''}`}
            onClick={() => setSelectedBiweek(null)}
          >
            Full Period
          </button>
          {selectedPeriod.biweeks.map((bw, index) => (
            <button
              key={bw.startDate}
              className={`kpi-biweek-tab ${selectedBiweek === index ? 'active' : ''}`}
              onClick={() => setSelectedBiweek(index)}
            >
              {bw.label}
            </button>
          ))}
        </div>
      )}

      {/* Dashboard Content */}
      <div className="kpi-board-content">
        {renderDashboard()}
      </div>
    </div>
  );
};

export default KPIBoard;
